/*
 * Wrapper for Auth0 authentication service and session management functionality
 * Based on https://manage.auth0.com/#/clients/dw1cdG64v18guKwg7nRRE8bcPP4WNkag/quickstart
*/

// import { push } from 'react-router-redux';
import * as auth0 from 'auth0-js';
import Bluebird from 'bluebird';
import * as _ from 'lodash';
import * as request from 'superagent';

import * as AppUtilities from '../common/apputilities';
import { Formatters } from '../common/formatters';
import { NavigationLinks } from '../common/navigation';
import ApiEndpoints from '../config/apiendpoints';
import { AuthConfig } from '../config/authconfiguration';
import AppConfiguration from '../config/configuration';
import history from '../history';

export default class Auth {
  public constructor() {
    this.config = AppConfiguration.getAuth0Configuration();

    this.auth0 = new auth0.WebAuth(this.config);
  }

  private auth0: auth0.WebAuth;
  private config: any = {};

  public enforceAuthentication(routeLocation?: any, navigateTo?: any) {
    // Enforces authentication (used by react-router Route tag)
    if (!routeLocation || !routeLocation.hash) { return; }

    if (/access_token|id_token|error/.test(routeLocation.hash)) {
      return this.handleAuthentication(navigateTo);
    }

    return;
  }

  public login() {
    // Start auth0 authorization workflow
    this.auth0.authorize({});
  }

  public logout(redirectToPathAfterLogin?: string) {
    // Clear access token and ID token from local storage
    // Note: this does not actually end the session on auth0's side, this is intentional on their part
    this.clearAllLocalStorageValues();
    this.clearRedirectToAfterLogin();

    this.setLocalStorageValue(AuthConfig.LocalStorageTokens.loggedOutAt, `${new Date().getTime()}`);

    if (redirectToPathAfterLogin) {
      this.setRedirectToAfterLogin(redirectToPathAfterLogin);
    }

    const config = AppConfiguration.getAuth0Configuration();
    const logoutReturnTo = Formatters.getStr(config, 'logoutRedirectTo');

    const logoutOptions = {
      returnTo: logoutReturnTo,
    };

    // console.log(`auth.logout logoutOptions.returnTo: ${logoutReturnTo}`);

    this.auth0.logout(logoutOptions);
  }

  /* LocalStorage management functions */
  public clearAllLocalStorageValues() {
    // Clears/removes all known local storage values

    localStorage.removeItem(AuthConfig.LocalStorageTokens.accessToken);
    localStorage.removeItem(AuthConfig.LocalStorageTokens.idToken);
    localStorage.removeItem(AuthConfig.LocalStorageTokens.expiresAt);
    localStorage.removeItem(AuthConfig.LocalStorageTokens.loggedOutAt);
    localStorage.removeItem(AuthConfig.LocalStorageTokens.profile);
    localStorage.removeItem(AuthConfig.LocalStorageTokens.passwordChangeRequired);
  }

  public getLocalStorageValue(token: string) {
    // Returns a local storage value by token name

    if (!token) { return; }

    return localStorage.getItem(token);
  }

  public setLocalStorageValue(token: string, value: any) {
    // Store a value in local storage

    // if (!token) { return; }

    localStorage.setItem(token, value);
  }

  public clearLocalStorageValue(token: string) {
    // Clears/removes previously local storage value for token
    localStorage.removeItem(token);
  }

  /* Session management functions */
  public getUserRoles() {
    // Returns all roles attached to current user session
    return _.get(this.getSession(), AuthConfig.ProfilePaths.userRoles);
  }

  public userHasRole(role: string) {
    // Returns true if current user session has specified role assigned

    let result = false;

    if (this.isAuthenticated()) {
      const roles = this.getUserRoles();

      if (AppUtilities.isArrayWithLength(roles)) {
        result = _.find(roles, [AuthConfig.UserRoles.roleName, role]) ? true : false;
      }
    }

    return result;
  }

  public isAuthenticated() {
    // Check whether the current time is past the access token's expiry time
    let authenticated = false;

    const expiresAt = this.getLocalStorageValue(AuthConfig.LocalStorageTokens.expiresAt);
    if (expiresAt) {
      try {
        const expiresJson = Number(JSON.parse(expiresAt));
        authenticated = new Date().getTime() < expiresJson;
      } catch (ex) {
        authenticated = false;
      }
    }

    return authenticated;
  }

  public isLoggedOut() {
    let loggedOut = false;

    const loggedOutAt = this.getLocalStorageValue(AuthConfig.LocalStorageTokens.loggedOutAt);
    if (loggedOutAt) {
      try {
        const logoutJson = Number(JSON.parse(loggedOutAt));
        loggedOut = new Date().getTime() > logoutJson;
      } catch (ex) {
        loggedOut = false;
      }
    }

    return loggedOut;
  }

  public isPasswordChangeRequired() {
    let result = false;

    const sessionPasswordChangeRequired = this.getLocalStorageValue(AuthConfig.LocalStorageTokens.passwordChangeRequired);
    if (sessionPasswordChangeRequired) {
      try {
        const reqBool = Boolean(JSON.parse(sessionPasswordChangeRequired));
        result = (sessionPasswordChangeRequired) ? true : false;
      } catch (ex) {
        result = false;
      }
    }

    return result;
  }

  public isAdmin() {
    // Returns true if current user session has admin role assigned
    return this.userHasRole(AuthConfig.UserRoles.administrator);
  }

  public isEmployee() {
    // Returns true if current user session has employee role assigned
    return this.userHasRole(AuthConfig.UserRoles.employee);
  }

  public isClient() {
    // Returns true if current user session has client or client_read_only roles assigned
    let result = false;

    const hasClientRole = this.userHasRole(AuthConfig.UserRoles.client);
    const hasClientReadOnlyRole = this.userHasRole(AuthConfig.UserRoles.clientReadOnly);

    result = (hasClientRole || hasClientReadOnlyRole) ? true : false;

    return result;
  }

  public setSession(authResult: auth0.Auth0DecodedHash, profile: any) {
    if (!authResult.expiresIn || !authResult.accessToken || !authResult.idToken) {
      // console.log('setSession: invalid authResult parameter', authResult);
      return;
    }

    if (!profile) {
      // console.log('setSession: invalid profile parameter', profile);
      return;
    }

    // Set the time that the access token will expire at
    const expiresAt = JSON.stringify((authResult.expiresIn * 1000) + new Date().getTime());
    const passwordChangeRequired = Formatters.getBool(authResult, AuthConfig.ProfilePaths.userPasswordChangeRequired);

    this.setLocalStorageValue(AuthConfig.LocalStorageTokens.accessToken, _.get(authResult, 'accessToken', ''));
    this.setLocalStorageValue(AuthConfig.LocalStorageTokens.idToken, _.get(authResult, 'idToken', ''));
    this.setLocalStorageValue(AuthConfig.LocalStorageTokens.expiresAt, expiresAt);
    this.setLocalStorageValue(AuthConfig.LocalStorageTokens.loggedOutAt, '');
    this.setLocalStorageValue(AuthConfig.LocalStorageTokens.profile, JSON.stringify(profile));
    this.setLocalStorageValue(AuthConfig.LocalStorageTokens.passwordChangeRequired, passwordChangeRequired);
  }

  public getSession() {
    // Returns an object with auth0 details for current session
    let result = {
      access_token: '',
      id_token: '',
      expires_at: '',
      logged_out_at: this.getLocalStorageValue(AuthConfig.LocalStorageTokens.loggedOutAt),
      profile: undefined,
      passwordChangeRequired: false,
    };

    if (this.isAuthenticated()) {
      result = Object.assign({}, result, {
        access_token: this.getLocalStorageValue(AuthConfig.LocalStorageTokens.accessToken),
        id_token: this.getLocalStorageValue(AuthConfig.LocalStorageTokens.idToken),
        expires_at: this.getLocalStorageValue(AuthConfig.LocalStorageTokens.expiresAt),
        logged_out_at: this.getLocalStorageValue(AuthConfig.LocalStorageTokens.loggedOutAt),
        profile: JSON.parse(this.getLocalStorageValue(AuthConfig.LocalStorageTokens.profile) + ''),
        passwordChangeRequired: (this.getLocalStorageValue(AuthConfig.LocalStorageTokens.passwordChangeRequired)) ? true : false,
      });
    }

    return result;
  }

  public getSessionUsername() {
    // Returns current user session's nickname

    return _.get(this.getSession(), AuthConfig.ProfilePaths.userProfileNickname, '');
  }

  public getSessionUserObjectId() {
    // Returns current user session's object id

    return _.get(this.getSession(), AuthConfig.ProfilePaths.userProfileObjectId, '');
  }

  /* Post login automatic redirection functionality  */
  public setRedirectToAfterLogin(path?: string) {
    // Store the desired app path to redirect user to after next login
    if (!path) { return; }

    this.setLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAfterLogin, path);
  }

  public clearRedirectToAfterLogin() {
    // Clears/removes previously set redirect path
    localStorage.removeItem(AuthConfig.LocalStorageTokens.redirectToAfterLogin);
  }

  public getRedirectToAfterLogin() {
    // Returns app path to redirect to after a login
    let result = NavigationLinks.userDashboard();

    const redirectPath = this.getLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAfterLogin);
    if (redirectPath) { result = '' + redirectPath; }

    return result;
  }

  public getCurrentViewPath() {
    // Returns current app path name based on browser window location
    return window.location.pathname;
  }

  /* Private helper functions */
  private handleAuthentication(navigateTo?: any) {
    // Handle the auth0 login response, set last login, redirect to app path
    const getParsedHash: any = Bluebird.promisify(this.auth0.parseHash, { context: this.auth0 });
    const getUserInfo: any = Bluebird.promisify(this.auth0.client.userInfo, { context: this.auth0.client });

    const gotoUrl = (url) => {
      if (navigateTo) {
        navigateTo(url);
      } else {
        history.push(url);
      }
    };

    return getParsedHash()
      .then((authResult, err) => {
        if (err || !authResult || !authResult.accessToken || !authResult.idToken) {
          // console.log('auth.handleAuthentication: parseHash error', err);

          return gotoUrl(NavigationLinks.userLogin());
        }

        return getUserInfo(Formatters.getStr(authResult, 'accessToken'))
          .then((profile, infoErr) => {
            if (infoErr || !profile) {
              // console.log('auth.handleAuthentication getUserInfo error or missing profile:', infoErr);
              return gotoUrl(NavigationLinks.userLogin());
            }

            this.setSession(authResult, profile);

            // Call api to update user's lastLogin
            return request
              .get(ApiEndpoints.getUpdateUserLastLoginUrl())
              .set('Authorization', `Bearer ${authResult.accessToken}`);
          })
          .then((reqResult) => {
            if (Formatters.getInt(reqResult, 'statusCode') !== 200) {
              // console.log('auth.handleAuthentication updateUserLastLogin return error, logging out');
              return this.logout();
            }

            const passwordChangeRequired = this.isPasswordChangeRequired();

            // console.log(`auth.handleAuthentication: parseHash updateUserLastLogin passwordChangeRequired ${passwordChangeRequired}`, { authResult, reqResult, passwordChangeRequired });

            let url = this.getRedirectToAfterLogin();
            this.clearRedirectToAfterLogin();

            // if (passwordChangeRequired) {
            //   const userObjId = this.getSessionUserObjectId();
            //   url = NavigationLinks.userEditObjId(userObjId);
            //   console.log(`auth.handleAuthentication: parseHash updateUserLastLogin passwordChangeRequired REDIRECT url ${url} userobjid ${userObjId}`);
            // } else {
            //   this.clearRedirectToAfterLogin();
            // }

            // console.log(`auth.handleAuthentication.getParsedHash.getUserInfo gotoUrl ${url}`);
            return gotoUrl(url);
          });
      });
  }
}



// WEBPACK FOOTER //
// ./src/auth/auth.tsx